import React from "react";
import { toast } from "sonner";
import { Award, Check, Heart, MessageSquareQuote, SlidersHorizontal, X } from "lucide-react";
import { ClientFactory } from "@/main/common/ClientFactory";
import { logApiFailure } from "@/main/common/ApiUtils";
import {
    ProfileWallEntry,
    ProfileWallEntryKind,
    ProfileWallPage,
    profileWallEntryKey,
} from "@/main/common/ProfileWallTypes";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { FAMILY_COPY } from "@/main/react/user/achievementCopy";
import { Localizer } from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import { localizeWithMemberName, localizeWithMemberNames } from "@/main/react/component/localizeWithMemberName";

/**
 * The profile wall: one reverse-chronological stream of what the product and other members have said
 * about this member.
 *
 * <h2>It is a SECTION, never an island</h2>
 *
 * `user.jte` has exactly one `data-component` container (`UserPage`, line 60) and its own comment
 * records why: `createRoot()` discards its element's children, so a `react-component` nested inside
 * another one is deleted the instant the parent chunk resolves. That defect presents as FLAKINESS, not
 * absence — the standalone moderation panel spent its short life this way in 2026-07 — so this file is
 * a plain component rendered by `UserPage`, and the browser spec asserts it is still present AFTER the
 * sibling island mounted rather than merely present.
 *
 * <h2>Rule 32: it may not subtract from the page</h2>
 *
 * Two consequences, and neither is a defensive extra:
 *
 *  - **A failed load renders an error panel and nothing else is disturbed.** The profile's bio, rail,
 *    section strip and server-rendered discussion list are outside this component, so a terminal fetch
 *    failure costs the wall and only the wall.
 *  - **An empty wall renders nothing at all to a VISITOR.** Only the owner sees an empty state. This is
 *    the trophy rail's rule applied to a second surface: "this person has received nothing" is a
 *    negative displayed about a stranger, and the achievements design already banned it once
 *    (an empty case reads as an accusation on a new profile). `UserPage` reads
 *    `onEmptinessResolved` and falls back to the sections view, so a visitor to an empty profile gets
 *    the posts hub rather than a box saying nothing is here.
 *
 * <h2>What it deliberately does not have</h2>
 *
 * No composer, no comments, no approval gate, no visible endorser count. Those are phases 3 and 4 and
 * each is a separate decision the operator has not taken.
 *
 * <h2>And no per-kind switches: it POINTS at them</h2>
 *
 * The two toggles live in `/settings/content_preferences` as `ProfileWallPreferencesForm` (design §6.3,
 * operator decision 2026-08-29). The wall's owner-only link is the SETTINGS-44 shape — the theme
 * control has had two homes since wave 8 — with the load-bearing half kept: one store, and the second
 * surface is a pointer rather than a second copy. Two copies of one boolean, each with an effect that
 * writes the other, is precisely the shape rule 31 forbids.
 *
 * So this component reads `showAchievements`/`showEndorsements` from NOWHERE. The server has already
 * applied them to the stream it returned; there is nothing here to keep in sync, and adding a local
 * copy of either would re-create the defect the decision avoided.
 *
 * Design: designs/00-inbox/2026-08-25-profile-wall-unified-activity-timeline-design.md §2, §6, §10.1.
 */

const KIND_ICON: Record<ProfileWallEntryKind, typeof Award> = {
    ENDORSEMENT: Heart,
    ACHIEVEMENT: Award,
    ANSWER_ACCEPTED: Check,
    MIND_CHANGED: MessageSquareQuote,
};

type LoadState = "loading" | "ready" | "failed";

/**
 * Round 42 D12 (`PROFILE-25`, U58): how long the "Removed from your wall." toast offers Undo before the
 * DELETE is sent. The same 10 s the settings undo toasts use (`SETTINGS_UNDO_WINDOW_MS`).
 */
export const PROFILE_WALL_UNDO_WINDOW_MS = 10_000;

/** A removal waiting out its Undo window. `flush` sends the DELETE (once); `cancel` drops it (Undo). */
type PendingRemoval = { flush: () => void; cancel: () => boolean };

export type ProfileWallProps = {
    username: string;
    localizer: Localizer;
    /**
     * The entry the address bar is pointing at, or null. It is `UserPage`'s route state rather than a
     * fragment, so it survives Back — see rule 33 and P8.
     */
    highlightedEntryKey?: string | null;
    /**
     * Told once per load whether the wall has anything on it, so the host can decide what the main
     * column shows. It is the host's decision and not this component's, because "render the posts hub
     * instead" is a statement about the page, not about the wall.
     */
    onEmptinessResolved?: (isEmpty: boolean) => void;
    /**
     * FI-618: the owner is previewing their profile as a visitor sees it, so the wall renders as it would
     * for a visitor: the server's `viewerIsOwner` and each entry's `viewerCanRemove` are masked. A
     * preview only; the endpoints still answer this member as the owner.
     */
    previewAsVisitor?: boolean;
};

export function ProfileWall(props: ProfileWallProps) {
    const { username, localizer, highlightedEntryKey, onEmptinessResolved, previewAsVisitor = false } = props;

    const [entries, setEntries] = React.useState<ProfileWallEntry[]>([]);
    const [nextCursor, setNextCursor] = React.useState<string | null>(null);
    const [state, setState] = React.useState<LoadState>("loading");
    const [serverSaysOwner, setViewerIsOwner] = React.useState(false);
    // FI-618: the owner's visitor preview masks the server's answer for rendering only.
    const viewerIsOwner = serverSaysOwner && !previewAsVisitor;
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [reloadKey, setReloadKey] = React.useState(0);

    // The host is told about emptiness through a ref-stable callback held in a ref, so that a host that
    // re-creates the callback on every render cannot re-trigger the load effect. Rule 31's shape,
    // applied to a plain prop: an effect that re-runs for a reason nobody intended is the same defect
    // one layer up from a store that notifies for a write that changed nothing.
    const emptinessRef = React.useRef(onEmptinessResolved);
    React.useEffect(() => {
        emptinessRef.current = onEmptinessResolved;
    }, [onEmptinessResolved]);

    React.useEffect(() => {
        if (!username) {
            return undefined;
        }
        let cancelled = false;
        setState("loading");

        ClientFactory.getProfileWall(username)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const page: ProfileWallPage = response.data;
                setEntries(page.entries ?? []);
                setNextCursor(page.nextCursor ?? null);
                setViewerIsOwner(Boolean(page.viewerIsOwner));
                setState("ready");
                emptinessRef.current?.((page.entries ?? []).length === 0);
            })
            .catch((error) => {
                if (cancelled) {
                    return;
                }
                logApiFailure("profile-wall-read", error);
                setState("failed");
                // A FAILED load is not an empty wall, and the host must not treat it as one. Reporting
                // "empty" here would swap the main column to the posts hub on a transient blip, which is
                // a page that silently changed what it was showing because a fetch was slow.
                emptinessRef.current?.(false);
            });

        return () => {
            cancelled = true;
        };
    }, [username, reloadKey]);

    const loadMore = React.useCallback(() => {
        if (!nextCursor || loadingMore) {
            return;
        }
        setLoadingMore(true);
        ClientFactory.getProfileWall(username, nextCursor)
            .then((response) => {
                const page: ProfileWallPage = response.data;
                // Concatenated, never merged by id: the server's keyset guarantees a page cannot repeat
                // a row it already served, and de-duplicating here would hide it if that ever broke.
                setEntries((previous) => [...previous, ...(page.entries ?? [])]);
                setNextCursor(page.nextCursor ?? null);
            })
            .catch((error) => {
                logApiFailure("profile-wall-page", error);
                // The already-loaded entries stay. A failed second page must never empty the first.
                setNextCursor(null);
            })
            .finally(() => setLoadingMore(false));
    }, [loadingMore, nextCursor, username]);

    /*
     * Round 42 D12 (`PROFILE-25`, U58): a DEFERRED delete, because there is no restore endpoint. The row
     * hides at once and the toast offers Undo; the DELETE goes out only when the toast closes without an
     * Undo (auto-close or dismiss), when the page is being left (`pagehide`), or when the wall unmounts,
     * whichever comes first, exactly once. The FirstSessionChecklist D7 shape (`pendingCloseRef`).
     *
     * `hiddenKeys` is a VIEW filter over `entries`, never a second copy of the list: the entry itself
     * stays in `entries` until the server has confirmed the removal, so Undo and a failed DELETE both
     * restore it by deleting one key from the set (rule 31's "never keep one value in two places").
     *
     * A failed DELETE restores the row and says so. The row used to stay put silently on failure, which
     * the 2026-09-28 evidence note on `PROFILE-25` recorded as the other half of the defect.
     */
    const [hiddenKeys, setHiddenKeys] = React.useState<ReadonlySet<string>>(() => new Set());
    const pendingRemovalsRef = React.useRef<Map<string, PendingRemoval>>(new Map());
    const unhide = React.useCallback((key: string) => {
        setHiddenKeys((previous) => {
            if (!previous.has(key)) {
                return previous;
            }
            const next = new Set(previous);
            next.delete(key);
            return next;
        });
    }, []);

    // A wall unmounted inside an Undo window (a soft navigation away, a section switch) still owes the
    // server every removal the member did not take back.
    React.useEffect(() => {
        const pending = pendingRemovalsRef.current;
        return () => {
            Array.from(pending.values()).forEach((removal) => removal.flush());
        };
    }, []);

    const removeEntry = React.useCallback((entry: ProfileWallEntry) => {
        const key = profileWallEntryKey(entry);
        if (pendingRemovalsRef.current.has(key)) {
            return;
        }
        setHiddenKeys((previous) => {
            if (previous.has(key)) {
                return previous;
            }
            const next = new Set(previous);
            next.add(key);
            return next;
        });

        let settled = false;
        const settle = () => {
            settled = true;
            window.removeEventListener("pagehide", flush);
            pendingRemovalsRef.current.delete(key);
        };
        function flush() {
            if (settled) {
                return;
            }
            settle();
            ClientFactory.removeProfileWallEntry(entry.kind, entry.sourceId)
                .then(() => {
                    setEntries((previous) => previous.filter((candidate) => profileWallEntryKey(candidate) !== key));
                    unhide(key);
                })
                .catch((error) => {
                    logApiFailure("profile-wall-remove", error);
                    unhide(key);
                    toast.error(localizer("error.unexpected"));
                });
        }
        /** True when the DELETE was still pending and is now dropped; false once it has gone out. */
        const cancel = (): boolean => {
            if (settled) {
                return false;
            }
            settle();
            return true;
        };
        window.addEventListener("pagehide", flush);
        pendingRemovalsRef.current.set(key, { flush, cancel });

        toast.success(localizer("profile.wall.removed"), {
            duration: PROFILE_WALL_UNDO_WINDOW_MS,
            action: {
                label: <span data-playwright-id="profile-wall-remove-undo">{localizer("literal.undo")}</span>,
                onClick: () => {
                    // Only a removal that has not been sent can be undone; afterwards it is gone for good.
                    if (cancel()) {
                        unhide(key);
                    }
                },
            },
            onAutoClose: flush,
            onDismiss: flush,
        });
    }, [localizer, unhide]);

    const visibleEntries = React.useMemo(
        () => (hiddenKeys.size === 0 ? entries : entries.filter((entry) => !hiddenKeys.has(profileWallEntryKey(entry)))),
        [entries, hiddenKeys],
    );

    if (state === "failed") {
        return (
            <section data-playwright-id="profile-wall" data-wall-state="failed" className="w-full">
                <PaginatedListEmptyState
                    variant="failed"
                    titleHeadingLevel={2}
                    title={localizer("profile.wall.error.title")}
                    description={localizer("profile.wall.error.description")}
                    action={{
                        label: localizer("profile.wall.error.retry"),
                        onClick: () => setReloadKey((previous) => previous + 1),
                    }}
                />
            </section>
        );
    }

    if (state === "loading") {
        // No skeleton: the wall is one of two things the main column can show, and a pulsing box that
        // then vanishes because the host switched to the posts hub is worse than a beat of nothing.
        return <section data-playwright-id="profile-wall" data-wall-state="loading" className="w-full" />;
    }

    if (visibleEntries.length === 0 && !viewerIsOwner) {
        // A visitor is shown NO wall on an empty profile. See the class note: never display a negative
        // about somebody else.
        return null;
    }

    return (
        <section
            data-playwright-id="profile-wall"
            data-wall-state="ready"
            data-wall-entry-count={visibleEntries.length}
            className="w-full"
            aria-labelledby="profile-wall-heading"
        >
            {/* Round 41 A39 (`EMPTY-STATES-130`, `PROFILE-77`): visually hidden, never deleted. The section
                pill directly above already reads "Wall", so a visible heading repeated it; the h2 stays
                as the region's name and one of the profile's few heading-navigation stops (`PROFILE-36`). */}
            <h2 id="profile-wall-heading" className="sr-only">
                {localizer("profile.wall.title")}
            </h2>

            {/*
              The wall's SECOND home for the per-kind toggles, and deliberately the thinner one: a
              pointer at the canonical control in /settings/content_preferences, never a second copy of
              it (wall design §6.3, operator decision 2026-08-29).

              The SETTINGS-44 precedent this follows — the theme control in the header sidebar and in
              /settings/appearance — works because both renderers read and write ONE store and neither
              keeps a copy. Two copies of a boolean on two surfaces is the shape rule 31 exists to
              forbid, so the discoverability half is paid with a link instead. The wall stays a surface
              that is content and nothing else, and the owner still reaches the setting from the thing
              it governs.

              Owner-only: `viewerIsOwner` comes from the server on the page payload, never from
              comparing a client-held username.
            */}
            {viewerIsOwner && (
                <a
                    href="/settings/content_preferences"
                    data-playwright-id="profile-wall-settings"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-text-main outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [@media(pointer:coarse)]:min-h-11"
                >
                    <SlidersHorizontal className="size-3.5" aria-hidden="true" />
                    {localizer("profile.wall.settings.title")}
                </a>
            )}

            {visibleEntries.length === 0 ? (
                <PaginatedListEmptyState
                    titleHeadingLevel={3}
                    title={localizer("profile.wall.empty.title")}
                    description={localizer("profile.wall.empty.description")}
                />
            ) : (
                <ul className="mt-3 flex w-full flex-col gap-2">
                    {visibleEntries.map((entry) => (
                        <ProfileWallEntryRow
                            key={profileWallEntryKey(entry)}
                            entry={entry}
                            localizer={localizer}
                            highlighted={highlightedEntryKey === profileWallEntryKey(entry)}
                            ownerUsername={username}
                            onRemove={removeEntry}
                            previewAsVisitor={previewAsVisitor}
                        />
                    ))}
                </ul>
            )}

            {nextCursor && (
                <button
                    type="button"
                    data-playwright-id="profile-wall-more"
                    className="mt-3 inline-flex items-center rounded-full border border-border px-3 py-1.5 text-xs font-medium text-text-main hover:bg-muted [@media(pointer:coarse)]:min-h-11"
                    onClick={loadMore}
                    disabled={loadingMore}
                >
                    {localizer("profile.wall.more")}
                </button>
            )}
        </section>
    );
}

function ProfileWallEntryRow(props: {
    entry: ProfileWallEntry;
    localizer: Localizer;
    highlighted: boolean;
    onRemove: (entry: ProfileWallEntry) => void;
    previewAsVisitor?: boolean;
    ownerUsername: string;
}) {
    const { entry, localizer, highlighted, onRemove, previewAsVisitor = false, ownerUsername } = props;
    const key = profileWallEntryKey(entry);
    // S7 A15 (`EMPTY-STATES-110`): the Open link is named "Open" + this row's sentence.
    const sentenceId = React.useId();
    const openId = React.useId();
    // S7 A17 (`EMPTY-STATES-115`): the payload always carried the time; the row now says it.
    const occurredLabel = React.useMemo(
        () => (entry.occurredAt ? new DateFormatter(localizer).timeSince(entry.occurredAt) : ""),
        [entry.occurredAt, localizer],
    );
    const Icon = KIND_ICON[entry.kind] ?? Award;

    return (
        <li
            // The anchor P8's per-entry deep link targets. Built by `profileWallEntryKey` — the SAME
            // function the address builder uses — so the id on the page and the id in the URL cannot
            // drift apart; two copies of that mapping is how a deep link comes to point at an element
            // that is on the page under a different name, which passes every URL-shaped test in full.
            id={`wall-entry-${key}`}
            data-playwright-id="profile-wall-entry"
            data-wall-entry-kind={entry.kind}
            data-wall-entry-key={key}
            // A stable data attribute rather than a class string or a colour: rule 20 requires the deep
            // link to be asserted on its EFFECT, and an effect asserted through a class name is an
            // assertion about styling.
            data-wall-entry-highlighted={highlighted ? "true" : "false"}
            // `relative`: the containing block the entry link's stretched layer fills (round 41 A38).
            className={`relative flex w-full items-start gap-3 rounded-xl border px-3 py-2 ${
                highlighted ? "border-primary bg-muted" : "border-border bg-card"
            }`}
        >
            <span aria-hidden="true" className="mt-0.5 text-muted-foreground">
                <Icon size={16} />
            </span>
            <div className="min-w-0 flex-1">
                <p id={sentenceId} className="text-sm leading-6 text-text-main">{describeEntry(entry, localizer, ownerUsername)}</p>
                {entry.kind === "ENDORSEMENT" && entry.detail2 && (
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{entry.detail2}</p>
                )}
                <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    {occurredLabel ? (
                        <time dateTime={entry.occurredAt} data-playwright-id="profile-wall-entry-time">{occurredLabel}</time>
                    ) : null}
                    {entry.discussionPublicId && (
                        // Round 41 A38 (`PROFILE-75`): the whole entry is the target, not a ~30px word.
                        // `after:absolute after:inset-0` stretches THIS link over the entry (the
                        // `NotificationItem` / `ThoughtCardDisplayer` card-link shape), so there is still
                        // exactly one link with one name; the × below is lifted above the layer.
                        <a
                            className="inline-flex items-center text-xs font-medium text-muted-foreground underline [@media(pointer:coarse)]:min-h-11 after:absolute after:inset-0 after:rounded-xl after:content-['']"
                            data-playwright-id="profile-wall-entry-link"
                            href={entryHref(entry)}
                            aria-labelledby={`${openId} ${sentenceId}`}
                        >
                            <span id={openId}>{localizer("profile.wall.entry.link")}</span>
                        </a>
                    )}
                </div>
            </div>
            {entry.viewerCanRemove && !previewAsVisitor && (
                <button
                    type="button"
                    data-playwright-id="profile-wall-remove"
                    aria-label={localizer("profile.wall.remove")}
                    title={localizer("profile.wall.remove")}
                    // `PROFILE-25` (S7 A68): the glyph stays small, the TARGET does not. `touch-hit-44`
                    // widens the hit area to 44x44 on a coarse pointer without moving the layout, and it
                    // needs the `relative` containing block it deliberately does not set itself.
                    // Round 41 A38: `z-10` lifts it above the entry link's stretched layer, so the
                    // large area navigates and only this small one removes.
                    className="relative z-10 touch-hit-44 rounded-full p-1 text-muted-foreground hover:bg-muted"
                    onClick={() => onRemove(entry)}
                >
                    <X size={14} />
                </button>
            )}
        </li>
    );
}

/**
 * One sentence per kind, whole rather than assembled from fragments.
 *
 * A sentence split across elements cannot be translated: word order, possessives and quotation marks
 * all move between languages, and a per-fragment catalog freezes English grammar into the layout.
 */
function describeEntry(entry: ProfileWallEntry, localizer: Localizer, owner: string): React.ReactNode {
    const actor = entry.actorUsername ?? "";
    const title = entry.title ?? "";
    // S7 C13 (`EMPTY-STATES-139`): `{2}` is the wall's owner, whose reply the credit went to.
    // FI-792 (2026-09-30): the handles go through `localizeWithMemberNames`, so each name sits in its own
    // `translate="no"` span and the copy around it stays translatable; the title is our reader's text,
    // passed as own copy (rendered unmarked, exactly as the plain string rendered it).

    switch (entry.kind) {
        case "ENDORSEMENT":
            return title
                ? localizeWithMemberNames(localizer, "profile.wall.entry.endorsement", [actor, { ownCopy: title }, owner])
                : localizeWithMemberName(localizer, "profile.wall.entry.endorsement.no_title", actor);
        case "ANSWER_ACCEPTED":
            return title
                ? localizeWithMemberNames(localizer, "profile.wall.entry.answer_accepted", [actor, { ownCopy: title }])
                : localizeWithMemberName(localizer, "profile.wall.entry.answer_accepted.no_title", actor);
        case "MIND_CHANGED":
            // Round 38 D25 (EMPTY-STATES-118): titled like its siblings, untitled as the fallback.
            return title
                ? localizeWithMemberNames(localizer, "profile.wall.entry.mind_changed_titled", [actor, { ownCopy: title }, owner])
                : localizeWithMemberName(localizer, "profile.wall.entry.mind_changed", actor);
        case "ACHIEVEMENT":
        default:
            // detail1 is the family key, which is stable and never renamed; the catalog holds the
            // human name per family. A family this build does not know renders its key rather than
            // an empty sentence, which is the honest degradation for a register that moved.
            return localizer("profile.wall.entry.achievement", achievementName(entry.detail1, localizer));
    }
}

/**
 * The family's human name, through the SAME literal table the rail and the cabinet dialog read.
 *
 * `FAMILY_COPY` exists precisely so nobody builds `achievements.family.${key}.name` at runtime and
 * casts it: rule 11 bans that cast, and the ban has teeth here — because every key in that table is a
 * literal, adding a family to the backend register without adding its copy is a `tsc` error rather than
 * a profile rendering the raw string "achievements.family.S1.name" at a member.
 *
 * A family this build does not know degrades to its stable key, which is honest for a register that
 * moved ahead of the frontend, rather than to an empty sentence.
 */
function achievementName(familyKey: string | null, localizer: Localizer): string {
    if (!familyKey) {
        return "";
    }
    const copy = FAMILY_COPY[familyKey];
    return copy ? localizer(copy.name) : familyKey;
}

function entryHref(entry: ProfileWallEntry): string {
    if (entry.discussionPublicId && entry.thoughtPublicId) {
        // Canonical /thought/ shape, never /argument/: ArgumentToThoughtRedirectFilter 301s the legacy
        // form, so minting it made every profile-wall entry cost two fetches. Same fix as the
        // notification permalinks and ArgumentEmbed, 2026-09-12.
        return `/discussion/${encodeURIComponent(entry.discussionPublicId)}/thought/${encodeURIComponent(entry.thoughtPublicId)}`;
    }
    if (entry.discussionPublicId) {
        return `/discussion/${encodeURIComponent(entry.discussionPublicId)}`;
    }
    return "/discussions";
}

export default ProfileWall;
